import { io } from "socket.io-client"
import queryString from "query-string"
import { IPaginationReq } from "utils/interfaces"
import { getSocketsUrl } from "utils/getSocketsUrl"
import { TAGS, api } from "../../api"
import { missionsApi } from "../missionsAPI"
import { balanceEndpoints } from "../balanceAPI/balance.api"
import {
  ICanPurchaseResponse,
  IMySaleChestInstancesResponse,
  IPurchaseRequest,
  IPurchaseResponse,
  ISaleChest,
  ISaleChestConfig,
  SaleChestType
} from "./types"

const basePath = "/sale-chests"

interface ISaleChestStatsUpdate {
  weapon: {
    sold: number
    remaining: number
    total: number
    lastChestNumber: number
  }
  jewelry: {
    sold: number
    remaining: number
    total: number
    lastChestNumber: number
  }
}

export const saleChestApi = api.enhanceEndpoints({
  addTagTypes: [TAGS.SALE_CHEST, TAGS.BALANCE]
})

export const saleChestEndpoints = saleChestApi.injectEndpoints({
  endpoints: (builder) => ({
    getSaleChests: builder.query<ISaleChest[], void>({
      query: () => ({
        url: basePath,
        method: "GET"
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: TAGS.SALE_CHEST as const,
                id: _id
              })),
              { type: TAGS.SALE_CHEST, id: "LIST" }
            ]
          : [{ type: TAGS.SALE_CHEST, id: "LIST" }],
      async onCacheEntryAdded(
        _,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }
      ) {
        const saleChestsSocket = io(`${getSocketsUrl()}/sale_chests`, {
          transports: ["websocket"],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000
        })

        const reconnectListener = () => {
          dispatch(
            saleChestApi.util.invalidateTags([
              { type: TAGS.SALE_CHEST, id: "LIST" }
            ])
          )
        }

        saleChestsSocket.on("reconnect", reconnectListener)

        await cacheDataLoaded

        const statsListener = (event: string) => {
          const stats: ISaleChestStatsUpdate = JSON.parse(event)

          updateCachedData((draft) => {
            draft.forEach((chest) => {
              if (chest.type === SaleChestType.WEAPON && stats.weapon) {
                chest.remainingCount = stats.weapon.remaining
                chest.lastChestNumber = stats.weapon.lastChestNumber
              } else if (
                chest.type === SaleChestType.JEWELRY &&
                stats.jewelry
              ) {
                chest.remainingCount = stats.jewelry.remaining
                chest.lastChestNumber = stats.jewelry.lastChestNumber
              }
            })
          })
        }

        saleChestsSocket.on("sale_chests:stats-updated", statsListener)

        try {
          await cacheEntryRemoved
        } finally {
          saleChestsSocket.off("reconnect", reconnectListener)
          saleChestsSocket.off("sale_chests:stats-updated", statsListener)
          saleChestsSocket.disconnect()
        }
      }
    }),

    getSaleChestById: builder.query<ISaleChest, string>({
      query: (chestId) => ({
        url: `${basePath}/${chestId}`,
        method: "GET"
      }),
      providesTags: (_, __, chestId) => [{ type: TAGS.SALE_CHEST, id: chestId }]
    }),

    getSaleChestConfig: builder.query<ISaleChestConfig, void>({
      query: () => ({
        url: `${basePath}/config`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.SALE_CHEST, id: "CONFIG" }]
    }),

    getMySaleChestInstances: builder.query<
      IMySaleChestInstancesResponse,
      IPaginationReq
    >({
      query: ({ page, limit }) => ({
        url: `${basePath}/me?${queryString.stringify({ page, limit })}`,
        method: "GET"
      }),
      providesTags: (result, _, params) =>
        result
          ? [
              {
                type: TAGS.SALE_CHEST as const,
                id: `my-instances:page:${params.page}:limit:${params.limit}`
              },
              { type: TAGS.SALE_CHEST as const, id: "MY_INSTANCES" }
            ]
          : [{ type: TAGS.SALE_CHEST as const, id: "MY_INSTANCES" }]
    }),

    canPurchaseSaleChest: builder.query<ICanPurchaseResponse, void>({
      query: () => ({
        url: `${basePath}/can-purchase`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.SALE_CHEST, id: "CAN_PURCHASE" }]
    }),

    purchaseSaleChest: builder.mutation<IPurchaseResponse, IPurchaseRequest>({
      query: (data) => ({
        url: `${basePath}/purchase`,
        method: "POST",
        data
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        if (data.newBalance) {
          dispatch(
            balanceEndpoints.util.updateQueryData(
              "getBalance",
              undefined,
              () => data.newBalance!
            )
          )
        }

        dispatch(
          saleChestApi.util.invalidateTags([
            { type: TAGS.SALE_CHEST, id: "MY_INSTANCES" }
          ])
        )

        dispatch(
          missionsApi.util.invalidateTags([
            { type: TAGS.MISSIONS, id: "MY_CHESTS" }
          ])
        )
      }
    })
  })
})

export const {
  useGetSaleChestsQuery,
  useGetSaleChestByIdQuery,
  useGetSaleChestConfigQuery,
  useGetMySaleChestInstancesQuery,
  useCanPurchaseSaleChestQuery,
  usePurchaseSaleChestMutation
} = saleChestEndpoints
